import dayjs, { ConfigType } from 'dayjs';
import { FaceSheetDemographicsType } from './type';

export type ClientDocumentFileNameDetails = {
  enrollmentId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  clientName?: string | null;
};

const getInitialsFromFullName = (name: string): string => {
  const commaParts = name.split(',');
  if (commaParts.length > 1) {
    return `${commaParts[0].trim().charAt(0)}${commaParts[1].trim().charAt(0)}`;
  }
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0);
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`;
};

const formatFileNameDate = (date?: ConfigType | null): string => {
  if (date === undefined) return dayjs().format('MMDDYYYY');
  const parsedDate = dayjs(date);
  return parsedDate.isValid() ? parsedDate.format('MMDDYYYY') : '';
};

export const getClientDocumentFileName = (
  documentName: string,
  date?: ConfigType | ConfigType[] | null,
  clientDetails?: ClientDocumentFileNameDetails,
): string => {
  let enrollmentId = clientDetails?.enrollmentId ?? '';
  let initials = `${clientDetails?.lastName?.trim().charAt(0) ?? ''}${
    clientDetails?.firstName?.trim().charAt(0) ?? ''
  }`;

  if (!clientDetails) {
    try {
      const demographics = JSON.parse(
        localStorage.getItem('Demographics') ?? '{}',
      ) as FaceSheetDemographicsType;
      const referralId = localStorage.getItem('referralId');
      const referrals = JSON.parse(
        localStorage.getItem('clientReferralDetails') ?? '[]',
      ) as { referralId: string; dphId: string | null }[];
      enrollmentId =
        referrals.find((referral) => referral.referralId === referralId)
          ?.dphId ?? '';
      initials = `${demographics?.lastName?.trim().charAt(0) ?? ''}${
        demographics?.firstName?.trim().charAt(0) ?? ''
      }`;
    } catch {
      enrollmentId = '';
      initials = '';
    }
  }

  if (!initials && clientDetails?.clientName) {
    initials = getInitialsFromFullName(clientDetails.clientName);
  }

  const formattedDates = (Array.isArray(date) ? date : [date])
    .map(formatFileNameDate)
    .filter(Boolean)
    .join('_');
  const prefix = [
    enrollmentId?.trim(),
    initials.trim().toUpperCase(),
    formattedDates,
  ]
    .filter(Boolean)
    .join('_');
  const safeDocumentName = documentName
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s*_\s*/g, '_')
    .replace(/\s+/g, ' ')
    .trim();
  return prefix ? `${prefix}_${safeDocumentName}` : safeDocumentName;
};

export const extractFileType = (fileType: string): string => {
  const typeArray = fileType.split('/');
  if (typeArray.length > 1) {
    if (typeArray[0] === 'application') {
      return fileType.includes('pdf') ? 'pdf' : 'document';
    } else if (typeArray[0] === 'audio') {
      return 'audio';
    } else if (typeArray[0] === 'image') {
      return 'photo';
    } else if (typeArray[0] === 'video') {
      return 'video';
    }
  }
  return 'other';
};

export const getFileNameFromURL = (url: string) => {
  return url.split('/').pop() ?? '-';
};

export const extractFileExtension = (fileUrl: string): string => {
  const newArray = fileUrl.split('?');
  const lastDotIndex = newArray[0].lastIndexOf('.');
  if (lastDotIndex === -1) {
    return '';
  }
  return newArray[0].substring(lastDotIndex);
};

export const dataURItoFile = (dataURI: string | null | undefined) => {
  // convert base64/URLEncoded data component to raw binary data held in a string
  if (dataURI === null || dataURI === undefined) {
    return null;
  } else {
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0)
      byteString = atob(dataURI.split(',')[1]);
    else byteString = decodeURIComponent(dataURI.split(',')[1]);

    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    // write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ia], { type: mimeString });
    const milliseconds = new Date().getUTCMilliseconds();
    const fileExtension = mimeString.split('/')[1] || 'jpg';
    return new File([blob], milliseconds + '.' + fileExtension, {
      type: mimeString,
    });
  }
};

export const convertFileToBase64 = (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    reader.readAsDataURL(file);
  });
};
